import React from "react";
import { 
  ArrowPathIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  ArrowTopRightOnSquareIcon 
} from "@heroicons/react/24/outline";
import T from "./T";

// ─── Stat Card (Reference Design) ─────────────────────────────────────────────
// variant / color: "default" | "green" | "red" | "blue" | "orange" | "purple" | "amber" | "gray"
export const StatCard = ({
  label,
  title,
  value,
  sub,
  subtext,
  icon: Icon,
  variant = "default",
  color,
  trend,
  trendDirection,
  onClick,
  active = false,
  isActive = false,
  viewDetailsText = "View Details",
  valueClassName,
  loading = false,
  href,
  disabled = false,
}) => {
  const displayLabel = label || title || "";
  const displaySub = sub || subtext || "";
  const activeVariant = (color || variant || "default").toLowerCase();
  const isSelected = Boolean(active || isActive);
  const isClickable = Boolean((onClick || href) && !disabled && !loading);
  const showAction = isClickable && viewDetailsText !== false;

  const valStr = String(value ?? "");
  const getValueSizeClass = () => {
    if (valueClassName) return valueClassName;
    if (valStr.length >= 16) return "text-xs sm:text-base font-bold";
    if (valStr.length >= 13) return "text-sm sm:text-lg font-bold";
    if (valStr.length >= 10) return "text-base sm:text-xl font-extrabold";
    if (valStr.length >= 8) return "text-lg sm:text-[22px] font-extrabold";
    return "text-xl sm:text-[26px] font-black";
  };

  const colorStyles = {
    default: {
      circle: "bg-orange-50 text-[#E8612C] dark:bg-orange-950/40 dark:text-[#E8612C]",
      borderHover: "hover:border-orange-200 dark:hover:border-orange-900/40",
      activeRing: "border-[#E8612C] dark:border-[#E8612C] ring-2 ring-[#E8612C]/25 shadow-sm",
      linkHover: "group-hover:text-[#E8612C]",
    },
    orange: {
      circle: "bg-orange-50 text-[#E8612C] dark:bg-orange-950/40 dark:text-[#E8612C]",
      borderHover: "hover:border-orange-200 dark:hover:border-orange-900/40",
      activeRing: "border-[#E8612C] dark:border-[#E8612C] ring-2 ring-[#E8612C]/25 shadow-sm",
      linkHover: "group-hover:text-[#E8612C]",
    },
    blue: {
      circle: "bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400",
      borderHover: "hover:border-sky-200 dark:hover:border-sky-900/40",
      activeRing: "border-sky-500 dark:border-sky-500 ring-2 ring-sky-500/25 shadow-sm",
      linkHover: "group-hover:text-sky-600 dark:group-hover:text-sky-400",
    },
    green: {
      circle: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400",
      borderHover: "hover:border-emerald-200 dark:hover:border-emerald-900/40",
      activeRing: "border-emerald-500 dark:border-emerald-500 ring-2 ring-emerald-500/25 shadow-sm",
      linkHover: "group-hover:text-emerald-600 dark:group-hover:text-emerald-400",
    },
    red: {
      circle: "bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400",
      borderHover: "hover:border-rose-200 dark:hover:border-rose-900/40",
      activeRing: "border-rose-500 dark:border-rose-500 ring-2 ring-rose-500/25 shadow-sm",
      linkHover: "group-hover:text-rose-600 dark:group-hover:text-rose-400",
    },
    purple: {
      circle: "bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400",
      borderHover: "hover:border-purple-200 dark:hover:border-purple-900/40",
      activeRing: "border-purple-500 dark:border-purple-500 ring-2 ring-purple-500/25 shadow-sm",
      linkHover: "group-hover:text-purple-600 dark:group-hover:text-purple-400",
    },
    amber: {
      circle: "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400",
      borderHover: "hover:border-amber-200 dark:hover:border-amber-900/40",
      activeRing: "border-amber-500 dark:border-amber-500 ring-2 ring-amber-500/25 shadow-sm",
      linkHover: "group-hover:text-amber-600 dark:group-hover:text-amber-400",
    },
    gray: {
      circle: "bg-gray-100 text-gray-600 dark:bg-[#21262d] dark:text-slate-400",
      borderHover: "hover:border-gray-300 dark:hover:border-slate-600",
      activeRing: "border-gray-400 dark:border-slate-500 ring-2 ring-gray-400/25 shadow-sm",
      linkHover: "group-hover:text-gray-700 dark:group-hover:text-slate-300",
    },
  };

  const s = colorStyles[activeVariant] || colorStyles.default;

  let trendEl = null;
  if (trend) {
    const isDown = trendDirection === "down" || String(trend).startsWith("-");
    trendEl = (
      <div className={`flex items-center gap-0.5 text-[10px] sm:text-xs font-bold shrink-0 ${isDown ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>
        {isDown ? (
          <ArrowTrendingDownIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.5} />
        ) : (
          <ArrowTrendingUpIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.5} />
        )}
        <span>{String(trend).replace(/^[+-]/, "")}</span>
      </div>
    );
  } else if (displaySub) {
    trendEl = (
      <span className="text-[9px] sm:text-[10px] font-semibold text-gray-500 dark:text-slate-400 bg-gray-100/80 dark:bg-[#21262d] px-1.5 sm:px-2 py-0.5 rounded-md shrink-0">
        <T>{displaySub}</T>
      </span>
    );
  }

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable && onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
      className={`bg-white dark:bg-[#161b22] border rounded-xl sm:rounded-2xl p-2.5 sm:p-5 shadow-xs transition-all duration-200 flex flex-col justify-between group ${
        isSelected
          ? s.activeRing
          : `border-gray-100 dark:border-[#30363d]/80 ${isClickable ? `${s.borderHover} hover:shadow-md cursor-pointer hover:-translate-y-0.5` : ""}`
      }`}
    >
      {/* Top Section: Icon on Left, Number + Label in Center, Trend on Right */}
      <div className="flex items-start justify-between gap-1.5 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-3.5 min-w-0">
          {Icon && (
            <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-full flex items-center justify-center shrink-0 ${s.circle} transition-transform duration-200 group-hover:scale-105`}>
              <Icon className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={1.8} />
            </div>
          )}
          <div className="min-w-0">
            {loading ? (
              <div className="h-5 sm:h-6 w-16 sm:w-24 bg-gray-200 dark:bg-slate-700 animate-pulse rounded my-0.5" />
            ) : (
              <h3
                title={valStr}
                className={`${getValueSizeClass()} tracking-tight text-gray-900 dark:text-white tabular-nums leading-none truncate`}
              >
                {value}
              </h3>
            )}
            <p className="text-[11px] sm:text-[13px] font-medium text-gray-500 dark:text-slate-400 mt-0.5 sm:mt-1 capitalize leading-tight truncate">
              <T>{displayLabel}</T>
            </p>
          </div>
        </div>

        {trendEl}
      </div>

      {/* Bottom Section: ONLY IF CLICKABLE */}
      {showAction && (
        <div className="border-t border-gray-100 dark:border-[#21262d] mt-2 sm:mt-3.5 pt-1.5 sm:pt-2.5 flex items-center justify-between">
          <span className={`text-[10px] sm:text-xs font-semibold ${isSelected ? "text-gray-900 dark:text-white font-bold" : `text-gray-400 dark:text-slate-400 ${s.linkHover}`} transition-colors truncate`}>
            <T>{isSelected ? "Active Filter" : viewDetailsText}</T>
          </span>
          <ArrowTopRightOnSquareIcon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 ${isSelected ? "text-gray-700 dark:text-slate-300" : `text-gray-400 dark:text-slate-500 ${s.linkHover}`} group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-150`} />
        </div>
      )}
    </div>
  );
};

// ─── Page Header ──────────────────────────────────────────────────────────────
export const PageHeader = ({ icon: Icon, iconColor = "text-[#E8612C]", title, subtitle, onRefresh, loading, children }) => (
  <div className="flex justify-between items-start sm:items-center gap-2 sm:gap-4 mb-3 sm:mb-4">
    <div className="min-w-0 flex-1 pr-1.5">
      <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-2.5 font-display leading-tight truncate">
        {Icon && <Icon className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 ${iconColor}`} />}
        <span className="truncate">{title}</span>
      </h1>
      {subtitle && <p className="text-[11px] sm:text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={loading}
          className="h-8 w-8 sm:w-auto px-0 sm:px-3 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] rounded-lg text-[11px] font-bold uppercase hover:bg-gray-50 dark:hover:bg-slate-800 transition shadow-2xs flex items-center justify-center gap-1.5 text-gray-700 dark:text-slate-300 cursor-pointer disabled:opacity-50 shrink-0"
          title="Refresh"
          aria-label="Refresh"
        >
          <ArrowPathIcon className={`w-4 h-4 sm:w-3.5 sm:h-3.5 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline"><T>Refresh</T></span>
        </button>
      )}
      {children}
    </div>
  </div>
);

// ─── Stats Grid ───────────────────────────────────────────────────────────────
export const StatsGrid = ({ stats, cols = 4, loading = false }) => {
  const colClass = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-4",
    5: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
    6: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
  }[cols] || "sm:grid-cols-2 lg:grid-cols-4";

  if (loading) {
    const skeletonCount = Array.isArray(stats) && stats.length > 0 ? stats.length : cols;
    return (
      <div className={`grid grid-cols-2 ${colClass} gap-2 sm:gap-4 mb-4 sm:mb-5`}>
        {Array.from({ length: skeletonCount }).map((_, i) => {
          const hasAction = stats && stats[i] && Boolean((stats[i].onClick || stats[i].href) && stats[i].viewDetailsText !== false);
          return (
            <div
              key={i}
              className="bg-white dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d]/80 rounded-xl sm:rounded-2xl p-2.5 sm:p-5 shadow-xs flex flex-col justify-between animate-pulse min-h-[75px] sm:min-h-[105px]"
            >
              <div className="flex items-start justify-between gap-2 sm:gap-3">
                <div className="flex items-center gap-2 sm:gap-3.5 min-w-0">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-full bg-gray-100 dark:bg-[#21262d] shrink-0" />
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="h-5 sm:h-6 w-16 sm:w-24 bg-gray-200 dark:bg-slate-700 rounded" />
                    <div className="h-2.5 sm:h-3 w-12 sm:w-16 bg-gray-100 dark:bg-slate-800 rounded" />
                  </div>
                </div>
              </div>
              {hasAction && (
                <div className="pt-1.5 sm:pt-3 border-t border-gray-100/80 dark:border-[#30363d]/60 mt-2 sm:mt-3 flex justify-between">
                  <div className="h-2 sm:h-2.5 w-12 sm:w-16 bg-gray-100 dark:bg-slate-800 rounded" />
                  <div className="h-2 sm:h-2.5 w-2 sm:w-2.5 bg-gray-100 dark:bg-slate-800 rounded" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 ${colClass} gap-2 sm:gap-4 mb-4 sm:mb-5`}>
      {stats.map((s, i) => <StatCard key={i} {...s} />)}
    </div>
  );
};

// ─── Search + Filter Toolbar ──────────────────────────────────────────────────
export const Toolbar = ({
  searchTerm,
  onSearch,
  searchPlaceholder = "Search...",
  onToggleFilters,
  showFilters,
  tabs,
  activeTab,
  onTabChange,
  children,
}) => (
  <div className="bg-transparent sm:bg-white dark:sm:bg-[#161b22] p-0 sm:p-3 rounded-none sm:rounded-xl border-0 sm:border border-gray-200 dark:border-[#30363d] shadow-none sm:shadow-2xs mb-2.5 sm:mb-4 flex flex-col sm:flex-row gap-2 sm:gap-2.5 justify-between items-stretch sm:items-center transition-colors">
    <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0 w-full">
      {onSearch && (
        <div className="relative flex-1 min-w-0">
          <svg className="w-4 h-4 text-gray-400 dark:text-slate-500 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            placeholder={searchPlaceholder}
            className={`w-full pl-8 sm:pl-9 ${onToggleFilters ? "pr-9 sm:pr-3" : "pr-3"} h-9 sm:h-8 border border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#E8612C] focus:border-[#E8612C] shadow-2xs sm:shadow-none transition`}
            value={searchTerm || ""}
            onChange={e => onSearch(e.target.value)}
          />
          {onToggleFilters && (
            <button
              type="button"
              onClick={onToggleFilters}
              title={showFilters ? "Hide Filters" : "Show Filters"}
              className={`sm:hidden absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-md transition cursor-pointer ${
                showFilters 
                  ? "bg-[#E8612C] text-white shadow-xs" 
                  : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-[#21262d]"
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" strokeWidth={2} />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* On mobile: view switcher and actions sit right alongside the search bar in the same row */}
      <div className="flex sm:hidden items-center gap-1 shrink-0">
        {children}
      </div>

      {/* Optional Segmented Tabs on Desktop */}
      {tabs && tabs.length > 0 && (
        <div className="hidden sm:flex items-center gap-1 bg-gray-100 dark:bg-[#21262d] p-0.5 rounded-lg border border-gray-200 dark:border-[#30363d] overflow-x-auto max-w-full">
          {tabs.map((tab) => {
            const val = typeof tab === "object" ? tab.value : tab;
            const label = typeof tab === "object" ? tab.label : tab;
            const count = typeof tab === "object" ? tab.count : undefined;
            const isActive = String(activeTab).toLowerCase() === String(val).toLowerCase();
            return (
              <button
                key={val}
                type="button"
                onClick={() => onTabChange && onTabChange(val)}
                className={`h-7 px-2.5 text-[11px] font-bold uppercase rounded-md transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  isActive
                    ? "bg-white dark:bg-[#161b22] text-[#E8612C] shadow-xs border border-gray-200 dark:border-[#30363d]"
                    : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <span>{label}</span>
                {count !== undefined && (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                      isActive
                        ? "bg-[#E8612C]/15 text-[#E8612C]"
                        : "bg-gray-200 dark:bg-[#30363d] text-gray-600 dark:text-slate-300"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>

    {/* Optional Segmented Tabs on Mobile */}
    {tabs && tabs.length > 0 && (
      <div className="sm:hidden flex items-center gap-1 bg-white dark:bg-[#161b22] p-0.5 rounded-lg border border-gray-200 dark:border-[#30363d] overflow-x-auto max-w-full">
        {tabs.map((tab) => {
          const val = typeof tab === "object" ? tab.value : tab;
          const label = typeof tab === "object" ? tab.label : tab;
          const count = typeof tab === "object" ? tab.count : undefined;
          const isActive = String(activeTab).toLowerCase() === String(val).toLowerCase();
          return (
            <button
              key={val}
              type="button"
              onClick={() => onTabChange && onTabChange(val)}
              className={`h-7 px-2 text-[10px] font-bold uppercase rounded-md transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                isActive
                  ? "bg-orange-50 dark:bg-[#21262d] text-[#E8612C] shadow-xs border border-orange-200 dark:border-[#30363d]"
                  : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <span>{label}</span>
              {count !== undefined && (
                <span
                  className={`text-[9px] px-1.5 py-0.2 rounded-full ${
                    isActive
                      ? "bg-[#E8612C]/15 text-[#E8612C]"
                      : "bg-gray-200 dark:bg-[#30363d] text-gray-600 dark:text-slate-300"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    )}

    {/* Desktop actions: Filters button + children */}
    <div className="hidden sm:flex gap-2 items-center shrink-0">
      {onToggleFilters && (
        <button
          type="button"
          onClick={onToggleFilters}
          className={`h-8 px-3 rounded-lg text-[11px] font-bold uppercase flex items-center gap-1.5 border transition cursor-pointer ${
            showFilters 
              ? "bg-[#E8612C]/10 text-[#E8612C] border-[#E8612C] dark:bg-[#E8612C]/20" 
              : "bg-white dark:bg-[#21262d] text-gray-700 dark:text-slate-300 border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-slate-800"
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <T>Filters</T>
        </button>
      )}
      {children}
    </div>
  </div>
);

// ─── Filter Panel / Drawer ────────────────────────────────────────────────────
export const FilterPanel = ({ isOpen, onClear, loading = false, isClearing = false, children }) => {
  if (!isOpen) return null;
  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 shadow-xs transition-colors relative overflow-hidden">
      {loading && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600 animate-pulse" />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
        {children}
      </div>
      {onClear && (
        <div className="mt-2.5 sm:mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClear}
            disabled={isClearing}
            className="text-xs text-red-500 dark:text-red-400 hover:underline cursor-pointer font-medium flex items-center gap-1.5 transition-opacity disabled:opacity-50"
          >
            {isClearing ? (
              <>
                <span className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin inline-block" />
                <T>Clearing...</T>
              </>
            ) : (
              <T>Clear All</T>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Standard Table Card Wrapper ──────────────────────────────────────────────
export const TableCard = ({ children, className = "" }) => (
  <div className={`flex-1 p-0 sm:p-4 bg-transparent sm:bg-white dark:sm:bg-[#161b22] border-0 sm:border border-gray-200 dark:border-[#30363d] rounded-none sm:rounded-xl shadow-none sm:shadow-xs overflow-visible sm:overflow-x-auto transition-colors ${className}`}>
    {children}
  </div>
);

// ─── Full Page Wrapper ─────────────────────────────────────────────────────────
const PageLayout = ({ children }) => (
  <div className="p-2.5 sm:p-6 bg-gray-50 dark:bg-[#0d1117] min-h-screen flex flex-col transition-colors duration-150">
    {children}
  </div>
);

export default PageLayout;